import {
  CALENDAR_START_HOUR,
  CALENDAR_END_HOUR,
  TOTAL_CALENDAR_HOURS
} from './constants';

export interface ServiceItem {
  quantity?: number;
  name?: string;
  type?: string;
  size?: string;
}

interface CalendarBooking {
  id: string;
  booking_date_sast: string;
  start_time: string;
  end_time?: string;
  [key: string]: unknown;
}

interface OperatorBlock {
  id: string;
  operator_id: string | null;
  start_date: string;
  end_date: string;
  start_time?: string | null;
  end_time?: string | null;
  is_all_day?: boolean;
  [key: string]: unknown;
}

interface CalendarFilters {
  search?: string;
  status?: string;
  territory_id?: string;
  assigned_to?: string;
}

interface CalendarEvent {
  type: 'booking' | 'block';
  id: string;
  data: CalendarBooking | OperatorBlock;
  startTime: string;
  endTime: string | null;
  startDecimal: number;
  endDecimal: number;
  column?: number;
  totalColumns?: number;
}

interface AvailabilityPattern {
  operator_id: string;
  is_active: boolean;
  days?: string[];
  start_time: string;
  end_time: string;
}

interface LegacyServicesObject {
  bed_count?: number;
  extras?: unknown[];
}

function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
}

/**
 * Parse the many historical `services` shapes into a bed count plus a list of
 * formatted extra services.
 *
 * Bed cleaning is recognised whether the count sits in the quantity (current
 * wizard, name "Bed Cleaning") or was typed into the name ("10 beds", "3 Bed
 * Service", "1 bed"); the early `{bed_count, extras}` object form is handled too.
 * A name-stated count is trusted over a contradictory quantity, and the two are
 * never multiplied together.
 */
function parseServices(services: unknown): { beds: number; extras: string[] } {
  let beds = 0;
  const extras: string[] = [];

  const addItem = (raw: ServiceItem) => {
    const qty = raw.quantity && raw.quantity > 0 ? raw.quantity : 1;
    const rawName = raw.name || (raw.type ? titleCase(raw.type) : '');
    // A leading number in the name carried the count ("10 beds", "3 Bed Service").
    const leading = rawName.match(/^\s*(\d+)\s*beds?\b/i);
    const isBed = raw.type === 'bed_cleaning'
      || !!leading
      || /bed cleaning/i.test(rawName)
      || /^\s*beds?\s*$/i.test(rawName);
    if (isBed) {
      // When the name already states the count ("10 beds"), trust it and ignore
      // a contradictory quantity. Only the generic bed line ("Bed Cleaning")
      // takes its count from quantity. Never multiply the two together.
      beds += leading ? parseInt(leading[1] as string, 10) : qty;
      return;
    }
    if (!rawName) return;
    const size = raw.size ? ` (${raw.size})` : '';
    const label = titleCase(rawName);
    extras.push(qty > 1 ? `${qty}x ${label}${size}` : `${label}${size}`);
  };

  if (Array.isArray(services)) {
    services.forEach(s => { if (s && typeof s === 'object') addItem(s as ServiceItem); });
  } else if (services && typeof services === 'object') {
    const obj = services as LegacyServicesObject;
    if (typeof obj.bed_count === 'number') beds += obj.bed_count;
    if (Array.isArray(obj.extras)) {
      obj.extras.forEach(e => {
        if (typeof e === 'string' && e.trim()) extras.push(titleCase(e));
        else if (e && typeof e === 'object') addItem(e as ServiceItem);
      });
    }
  }

  return { beds, extras };
}

/**
 * Format any historical `services` shape into one consistent label, e.g.
 * "3 beds" or "3 beds, 2x Mattress Protector". Used across the booking UI so a
 * booking reads the same in the list and when opened.
 */
export function formatServices(services: unknown): string {
  const { beds, extras } = parseServices(services);
  const parts: string[] = [];
  if (beds > 0) parts.push(`${beds} bed${beds === 1 ? '' : 's'}`);
  parts.push(...extras);
  return parts.length > 0 ? parts.join(', ') : '-';
}

/**
 * Extract just the bed count from any historical `services` shape, for loading
 * an existing booking into the bed picker when editing.
 */
export function bedCountFromServices(services: unknown): number {
  return parseServices(services).beds;
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string | null | undefined, maxLength = 20): string {
  if (!text) return '-';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '\u2026';
}

/**
 * Generate initials from a name (e.g., "Barry O'Sullivan" -> "BO", "Cape Town" -> "CT")
 */
export function getInitials(name: string | null | undefined, maxChars = 2): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return (parts[0] ?? '').substring(0, maxChars).toUpperCase();
  }
  return parts.slice(0, maxChars).map((p: string) => p[0]).join('').toUpperCase();
}

/**
 * Get territory abbreviation from territory name (removes "CleanMyBed " prefix)
 */
export function getTerritoryCode(territoryName: string | null | undefined): string | null {
  if (!territoryName) return null;
  // Remove "CleanMyBed " prefix if present
  const name = territoryName.replace(/^CleanMyBed\s*/i, '');
  return getInitials(name, 2);
}

/**
 * Format date to YYYY-MM-DD using LOCAL time (not UTC)
 * CRITICAL: Do NOT use toISOString() as it converts to UTC and shifts dates by timezone offset
 * Defined outside component so it's available before first render
 */
export function formatLocalDate(date: Date, timezone?: string): string {
  if (timezone) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(date);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse time string to decimal hours (e.g., "09:30" -> 9.5)
 */
export function parseTimeToDecimal(timeStr: string | null | undefined): number {
  if (!timeStr) return 0;
  const parts = timeStr.split(':').map(Number);
  const hours = parts[0] ?? 0;
  const minutes = parts[1] ?? 0;
  return hours + minutes / 60;
}

/**
 * Calculate vertical position percentage based on start time
 */
export function getTimePosition(startTime: string): number {
  const decimalTime = parseTimeToDecimal(startTime);
  const position = ((decimalTime - CALENDAR_START_HOUR) / TOTAL_CALENDAR_HOURS) * 100;
  return Math.max(0, Math.min(100, position));
}

/**
 * Calculate height percentage based on duration
 */
export function getTimeHeight(startTime: string, endTime: string | null | undefined, defaultDurationMinutes = 90): number {
  const startDecimal = parseTimeToDecimal(startTime);
  let endDecimal = parseTimeToDecimal(endTime);

  // If no end time, use default duration
  if (!endTime || endDecimal <= startDecimal) {
    endDecimal = startDecimal + (defaultDurationMinutes / 60);
  }

  const duration = endDecimal - startDecimal;
  const height = (duration / TOTAL_CALENDAR_HOURS) * 100;
  return Math.max(2, Math.min(100, height)); // Minimum 2% height
}

/**
 * Get all events (bookings + blocks) for a specific date with positioning info
 */
export function getEventsForDate(dateStr: string, filteredCalendarBookings: CalendarBooking[], showBlocks: boolean, operatorBlocks: OperatorBlock[], calendarFilters: CalendarFilters): CalendarEvent[] {
  const bookingEvents: CalendarEvent[] = filteredCalendarBookings.filter(b =>
    b.booking_date_sast === dateStr
  ).map(b => ({
    type: 'booking' as const,
    id: b.id,
    data: b,
    startTime: b.start_time,
    endTime: b.end_time || null,
    startDecimal: parseTimeToDecimal(b.start_time),
    endDecimal: parseTimeToDecimal(b.end_time) || parseTimeToDecimal(b.start_time) + 1.5
  }));

  const blockEvents: CalendarEvent[] = showBlocks ? operatorBlocks.filter(block => {
    if (dateStr < block.start_date || dateStr > block.end_date) return false;
    // When a specific operator is selected, show only their blocks. On the
    // "All" view show every operator's blocked time, the same way bookings do.
    if (calendarFilters.assigned_to !== 'all' && block.operator_id !== calendarFilters.assigned_to) return false;
    return true;
  }).map(block => ({
    type: 'block' as const,
    id: block.id,
    data: block,
    startTime: block.is_all_day ? `${CALENDAR_START_HOUR}:00` : (block.start_time || '08:00'),
    endTime: block.is_all_day ? `${CALENDAR_END_HOUR}:00` : (block.end_time || null),
    startDecimal: block.is_all_day ? CALENDAR_START_HOUR : parseTimeToDecimal(block.start_time),
    endDecimal: block.is_all_day ? CALENDAR_END_HOUR : parseTimeToDecimal(block.end_time)
  })) : [];

  return [...bookingEvents, ...blockEvents];
}

/**
 * Calculate overlapping columns for events
 */
export function calculateEventColumns(events: CalendarEvent[]): CalendarEvent[] {
  if (events.length === 0) return [];

  // Sort by start time, then by end time (shorter events first)
  const sorted = [...events].sort((a, b) => {
    if (a.startDecimal !== b.startDecimal) return a.startDecimal - b.startDecimal;
    return (a.endDecimal - a.startDecimal) - (b.endDecimal - b.startDecimal);
  });

  // Assign columns using a greedy algorithm
  const columns: CalendarEvent[][] = []; // Array of arrays, each inner array is events in that column
  const eventColumns = new Map<string, { column: number; totalColumns?: number }>(); // event id -> { column, totalColumns }

  sorted.forEach(event => {
    // Find the first column where this event doesn't overlap with any existing event
    let assignedColumn = -1;
    for (let col = 0; col < columns.length; col++) {
      const colEvents = columns[col]!;
      const overlaps = colEvents.some(e =>
        event.startDecimal < e.endDecimal && event.endDecimal > e.startDecimal
      );
      if (!overlaps) {
        assignedColumn = col;
        break;
      }
    }

    // If no column found, create a new one
    if (assignedColumn === -1) {
      assignedColumn = columns.length;
      columns.push([]);
    }

    columns[assignedColumn]!.push(event);
    eventColumns.set(`${event.type}-${event.id}`, { column: assignedColumn });
  });

  // Calculate total columns for each overlapping group
  sorted.forEach(event => {
    const key = `${event.type}-${event.id}`;
    const eventCol = eventColumns.get(key)!;

    // Find all events that overlap with this one
    const overlapping = sorted.filter(e =>
      event.startDecimal < e.endDecimal && event.endDecimal > e.startDecimal
    );

    // Get the maximum column index among overlapping events
    let maxCol = 0;
    overlapping.forEach(e => {
      const eCol = eventColumns.get(`${e.type}-${e.id}`);
      if (eCol) maxCol = Math.max(maxCol, eCol.column);
    });

    eventCol.totalColumns = maxCol + 1;
  });

  // Return events with column info
  return sorted.map(event => ({
    ...event,
    ...eventColumns.get(`${event.type}-${event.id}`)
  }));
}

/**
 * Get availability slots for rendering background (returns array of {startHour, endHour})
 */
export function getAvailabilitySlots(operatorId: string | null | undefined, date: Date, operatorPatterns: AvailabilityPattern[], dayNames: readonly string[]): { startHour: number; endHour: number }[] {
  if (!operatorId || operatorId === 'all') return [];

  const dayName = dayNames[date.getDay()] ?? '';
  return operatorPatterns
    .filter(p =>
      p.operator_id === operatorId &&
      p.is_active &&
      p.days?.includes(dayName)
    )
    .map(p => ({
      startHour: parseTimeToDecimal(p.start_time),
      endHour: parseTimeToDecimal(p.end_time)
    }));
}

/**
 * Get blocks for a specific date (for month/week views)
 */
export function getBlocksForDate(dateStr: string, showBlocks: boolean, operatorBlocks: OperatorBlock[], calendarFilters: CalendarFilters): OperatorBlock[] {
  if (!showBlocks) return [];

  return operatorBlocks.filter(block => {
    if (dateStr < block.start_date || dateStr > block.end_date) return false;
    // When a specific operator is selected, show only their blocks. On the
    // "All" view show every operator's blocked time, the same way bookings do.
    if (calendarFilters.assigned_to !== 'all' && block.operator_id !== calendarFilters.assigned_to) return false;
    return true;
  });
}
